import React, { FC } from 'react';
import styled from 'styled-components';
import {
    SkeletonBlock,
    SkeletonFiltersRow,
    SkeletonFooterBar,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface,
    SkeletonTable,
    SkeletonTableRow,
    SkeletonTabs
} from 'src/components/widgets/skeleton';

const QuoteSkeletonShell = styled(SkeletonPageShell).attrs({ $padding: '20px 0 92px' })``;

const QuoteSectionSkeleton = styled(SkeletonSurface).attrs({ $padding: '24px' })``;

const OverviewGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 24px;
    align-items: start;

    @media (max-width: 1200px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const QuoteFieldsGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px 28px;
    min-width: 0;

    @media (max-width: 760px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const QuoteAdditionalFieldSkeleton = styled.div`
    display: grid;
    grid-template-columns: 1.25fr 4.25fr 0.5fr;
    gap: 20px;
    align-items: center;
    min-width: 0;

    @media (max-width: 760px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const NotificationTemplateRowSkeleton = styled.div`
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr) auto;
    gap: 16px;
    align-items: center;
    padding: 16px 0;
    border-top: 1px solid #e0e0e0;

    &:first-child {
        border-top: none;
        padding-top: 0;
    }
`;

const SettingsToolbarSkeleton = styled(SkeletonSurface).attrs({ $padding: '14px 20px', $radius: '6px' })`
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
    margin-bottom: 20px;
`;

const EditorShellSkeleton = styled(SkeletonSurface).attrs({ $padding: '24px', $overflow: 'hidden' })`
    min-height: 520px;
    display: grid;
    grid-template-rows: auto auto minmax(320px, 1fr);
    gap: 18px;
`;

const QuoteFooterSkeleton = styled(SkeletonFooterBar).attrs({ $position: 'static', $padding: '16px 0 0' })`
    border-top: none;
`;

const FixedQuoteFooterSkeleton = styled(SkeletonFooterBar).attrs({ $position: 'fixed' })``;

const PageHeaderSkeleton: FC<{ titleWidth?: string; descriptionWidth?: string; actionWidth?: string }> = ({
    titleWidth = 'min(240px, 100%)',
    descriptionWidth = 'min(460px, 100%)',
    actionWidth
}) => (
    <SkeletonPageHeader>
        <SkeletonStack $gap='8px' $flex='1'>
            <SkeletonBlock $width={titleWidth} $height='26px' />
            <SkeletonBlock $width={descriptionWidth} $height='13px' />
        </SkeletonStack>
        {actionWidth && <SkeletonBlock $width={actionWidth} $height='36px' $radius='6px' />}
    </SkeletonPageHeader>
);

const FieldSkeleton: FC<{ labelWidth?: string }> = ({ labelWidth = '120px' }) => (
    <SkeletonStack $gap='8px'>
        <SkeletonBlock $width={labelWidth} $height='13px' />
        <SkeletonBlock $height='38px' $radius='6px' />
    </SkeletonStack>
);

const SectionHeaderSkeleton: FC<{ titleWidth?: string; descriptionWidth?: string }> = ({ titleWidth = '150px', descriptionWidth }) => (
    <SkeletonStack $gap='8px'>
        <SkeletonBlock $width={titleWidth} $height='20px' />
        {descriptionWidth && <SkeletonBlock $width={descriptionWidth} $height='13px' />}
    </SkeletonStack>
);

const QuoteRulesTableSkeleton: FC = () => (
    <SkeletonTable>
        <SkeletonTableRow $columns='minmax(0, 1fr) 72px' $padding='12px 18px' $minHeight='46px'>
            <SkeletonBlock $width='72px' $height='13px' />
            <SkeletonBlock $width='1px' $height='1px' />
        </SkeletonTableRow>
        {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonTableRow key={index} $columns='minmax(0, 1fr) 72px' $padding='12px 18px' $minHeight='52px'>
                <SkeletonBlock $width={index % 2 === 0 ? '220px' : '160px'} $height='15px' />
                <SkeletonRow $gap='8px' $justify='flex-end'>
                    <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                    <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                </SkeletonRow>
            </SkeletonTableRow>
        ))}
    </SkeletonTable>
);

const QuotesTableSkeleton: FC = () => (
    <SkeletonTable>
        <SkeletonTableRow
            $columns='1.4fr 0.6fr 1.4fr 2fr 0.6fr 1fr 0.2fr'
            $mobileColumns='minmax(0, 1fr) 84px'
            $mobileBreakpoint='900px'
            $padding='12px 18px'
            $minHeight='46px'
        >
            <SkeletonBlock $width='64px' $height='13px' />
            <SkeletonBlock $width='28px' $height='13px' />
            <SkeletonBlock $width='110px' $height='13px' />
            <SkeletonBlock $width='160px' $height='13px' />
            <SkeletonBlock $width='58px' $height='13px' />
            <SkeletonBlock $width='1px' $height='1px' />
            <SkeletonBlock $width='1px' $height='1px' />
        </SkeletonTableRow>
        {Array.from({ length: 7 }).map((_, index) => (
            <SkeletonTableRow key={index} $columns='1.4fr 0.6fr 1.4fr 2fr 0.6fr 1fr 0.2fr' $mobileColumns='minmax(0, 1fr) 84px' $mobileBreakpoint='900px'>
                <SkeletonBlock $width={index % 2 === 0 ? '210px' : '180px'} $height='15px' />
                <SkeletonBlock $width='48px' $height='15px' />
                <SkeletonBlock $width='min(210px, 100%)' $height='15px' />
                <SkeletonBlock $width='min(260px, 100%)' $height='15px' />
                <SkeletonBlock $width='92px' $height='22px' $radius='11px' />
                <SkeletonRow $justify='flex-end'>
                    <SkeletonBlock $width='62px' $height='15px' />
                </SkeletonRow>
                <SkeletonBlock $width='24px' $height='24px' $radius='4px' />
            </SkeletonTableRow>
        ))}
    </SkeletonTable>
);

export const QuoteRulesListSkeleton: FC = () => <QuoteRulesTableSkeleton />;

export const QuoteRequestsPreviewSkeleton: FC = () => <QuotesTableSkeleton />;

export const QuoteRequestsSkeleton: FC = () => (
    <>
        <SkeletonFiltersRow
            showSearch
            searchWidth='320px'
            controlWidths={['180px', '180px', '200px', '38px', '38px']}
            controlLabelWidths={['72px', '56px', null, null, null]}
            $margin='0 0 18px'
        />
        <QuotesTableSkeleton />
    </>
);

export const GetAQuotePageSkeleton: FC = () => (
    <QuoteSkeletonShell aria-hidden='true'>
        <PageHeaderSkeleton titleWidth='min(220px, 100%)' descriptionWidth='min(560px, 100%)' actionWidth='112px' />
        <OverviewGridSkeleton>
            <QuoteSectionSkeleton>
                <SkeletonStack $gap='18px'>
                    <SkeletonBlock $width='150px' $height='20px' />
                    <SkeletonBlock $height='70px' $radius='8px' />
                    <QuotesTableSkeleton />
                </SkeletonStack>
            </QuoteSectionSkeleton>
            <QuoteSectionSkeleton>
                <SkeletonStack $gap='12px'>
                    <SkeletonBlock $width='72px' $height='20px' />
                    {Array.from({ length: 3 }).map((_, index) => (
                        <SkeletonSurface key={index} $padding='16px' $radius='6px'>
                            <SkeletonStack $gap='12px'>
                                <SkeletonRow $justify='space-between'>
                                    <SkeletonBlock $width='140px' $height='15px' />
                                    <SkeletonBlock $width='52px' $height='15px' />
                                </SkeletonRow>
                                <SkeletonBlock $width='min(260px, 100%)' $height='12px' />
                                <SkeletonBlock $height='72px' $radius='6px' />
                            </SkeletonStack>
                        </SkeletonSurface>
                    ))}
                </SkeletonStack>
            </QuoteSectionSkeleton>
        </OverviewGridSkeleton>
    </QuoteSkeletonShell>
);

export const QuoteRulePageSkeleton: FC = () => (
    <QuoteSkeletonShell aria-hidden='true'>
        <PageHeaderSkeleton titleWidth='min(220px, 100%)' descriptionWidth='min(420px, 100%)' />
        <SkeletonStack $gap='18px'>
            <QuoteSectionSkeleton>
                <SkeletonStack $gap='18px'>
                    <SectionHeaderSkeleton titleWidth='86px' />
                    <FieldSkeleton labelWidth='76px' />
                    <SkeletonStack $gap='12px'>
                        <SkeletonRow $gap='10px'>
                            <SkeletonBlock $width='18px' $height='18px' $round />
                            <SkeletonBlock $width='min(320px, 100%)' $height='15px' />
                        </SkeletonRow>
                        <SkeletonRow $gap='10px'>
                            <SkeletonBlock $width='18px' $height='18px' $round />
                            <SkeletonBlock $width='min(360px, 100%)' $height='15px' />
                        </SkeletonRow>
                    </SkeletonStack>
                </SkeletonStack>
            </QuoteSectionSkeleton>
            <QuoteSectionSkeleton>
                <SkeletonStack $gap='14px'>
                    <SectionHeaderSkeleton titleWidth='132px' descriptionWidth='min(520px, 100%)' />
                    <SkeletonBlock $width='86px' $height='36px' $radius='6px' />
                </SkeletonStack>
            </QuoteSectionSkeleton>
            <QuoteFooterSkeleton>
                <SkeletonBlock $width='132px' $height='36px' $radius='6px' />
                <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
            </QuoteFooterSkeleton>
        </SkeletonStack>
    </QuoteSkeletonShell>
);

export const QuotePageSkeleton: FC = () => (
    <QuoteSkeletonShell aria-hidden='true'>
        <SkeletonPageHeader>
            <SkeletonRow $gap='12px'>
                <SkeletonBlock $width='min(210px, 100%)' $height='26px' />
                <SkeletonBlock $width='54px' $height='18px' />
                <SkeletonBlock $width='92px' $height='22px' $radius='11px' />
            </SkeletonRow>
        </SkeletonPageHeader>
        <SkeletonStack $gap='18px'>
            <QuoteSectionSkeleton>
                <SkeletonStack $gap='20px'>
                    <SectionHeaderSkeleton titleWidth='126px' descriptionWidth='min(440px, 100%)' />
                    <QuoteFieldsGridSkeleton>
                        <FieldSkeleton labelWidth='58px' />
                        <FieldSkeleton labelWidth='92px' />
                        <FieldSkeleton labelWidth='90px' />
                        <FieldSkeleton labelWidth='62px' />
                    </QuoteFieldsGridSkeleton>
                    <SkeletonBlock $width='min(220px, 100%)' $height='15px' />
                    <SkeletonStack $gap='10px'>
                        <SkeletonBlock $width='170px' $height='15px' />
                        <SkeletonTable>
                            {Array.from({ length: 4 }).map((_, index) => (
                                <SkeletonTableRow key={index} $columns='1.4fr 0.8fr 0.8fr 0.8fr' $mobileColumns='minmax(0, 1fr)'>
                                    <SkeletonBlock $width={index === 0 ? '80px' : 'min(180px, 100%)'} $height='14px' />
                                    <SkeletonBlock $width='min(120px, 100%)' $height='14px' />
                                    <SkeletonBlock $width='70px' $height='14px' />
                                    <SkeletonBlock $width='72px' $height='14px' />
                                </SkeletonTableRow>
                            ))}
                        </SkeletonTable>
                    </SkeletonStack>
                </SkeletonStack>
            </QuoteSectionSkeleton>
            <QuoteSectionSkeleton>
                <SkeletonStack $gap='18px'>
                    <SectionHeaderSkeleton titleWidth='190px' descriptionWidth='min(460px, 100%)' />
                    <SkeletonSurface $padding='16px' $radius='8px'>
                        <SkeletonStack $gap='16px'>
                            <QuoteFieldsGridSkeleton>
                                <FieldSkeleton labelWidth='88px' />
                                <FieldSkeleton labelWidth='90px' />
                            </QuoteFieldsGridSkeleton>
                            <SkeletonBlock $width='100px' $height='15px' />
                            <QuoteFieldsGridSkeleton>
                                <SkeletonBlock $height='38px' $radius='6px' />
                                <SkeletonBlock $height='38px' $radius='6px' />
                            </QuoteFieldsGridSkeleton>
                            <SkeletonBlock $width='120px' $height='15px' />
                            <QuoteAdditionalFieldSkeleton>
                                <SkeletonBlock $height='38px' $radius='6px' />
                                <SkeletonBlock $height='38px' $radius='6px' />
                                <SkeletonRow $gap='8px'>
                                    <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                                    <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                                </SkeletonRow>
                            </QuoteAdditionalFieldSkeleton>
                        </SkeletonStack>
                    </SkeletonSurface>
                </SkeletonStack>
            </QuoteSectionSkeleton>
            <QuoteFooterSkeleton>
                <SkeletonBlock $width='140px' $height='36px' $radius='6px' />
            </QuoteFooterSkeleton>
        </SkeletonStack>
    </QuoteSkeletonShell>
);

export const QuoteNotificationsSkeleton: FC = () => (
    <>
        <SettingsToolbarSkeleton>
            <SkeletonBlock $width='78px' $height='14px' />
            <SkeletonBlock $width='320px' $height='38px' $radius='6px' />
        </SettingsToolbarSkeleton>
        <QuoteSectionSkeleton>
            <SkeletonStack $gap='18px'>
                <SectionHeaderSkeleton titleWidth='210px' descriptionWidth='min(520px, 100%)' />
                <SkeletonRow $gap='16px'>
                    <SkeletonBlock $width='42px' $height='24px' $radius='12px' />
                    <SkeletonStack $gap='6px' $flex='1'>
                        <SkeletonBlock $width='210px' $height='15px' />
                        <SkeletonBlock $width='min(520px, 100%)' $height='13px' />
                    </SkeletonStack>
                </SkeletonRow>
                <SkeletonStack $gap='0'>
                    {Array.from({ length: 4 }).map((_, index) => (
                        <NotificationTemplateRowSkeleton key={index}>
                            <SkeletonBlock $width='18px' $height='18px' $radius='3px' />
                            <SkeletonStack $gap='5px'>
                                <SkeletonBlock $width={index % 2 === 0 ? '240px' : '190px'} $height='15px' />
                                <SkeletonBlock $width='min(520px, 100%)' $height='13px' />
                            </SkeletonStack>
                            <SkeletonBlock $width='86px' $height='15px' />
                        </NotificationTemplateRowSkeleton>
                    ))}
                </SkeletonStack>
                <SkeletonBlock $height='58px' $radius='8px' />
            </SkeletonStack>
        </QuoteSectionSkeleton>
    </>
);

export const QuoteSummarySkeleton: FC = () => (
    <>
        <SkeletonTabs tabWidths={['170px', '185px']} />
        <EditorShellSkeleton>
            <SkeletonRow $justify='space-between'>
                <SkeletonBlock $width='160px' $height='20px' />
                <SkeletonRow $gap='10px'>
                    <SkeletonBlock $width='82px' $height='34px' $radius='6px' />
                    <SkeletonBlock $width='88px' $height='34px' $radius='6px' />
                </SkeletonRow>
            </SkeletonRow>
            <QuoteFieldsGridSkeleton>
                <FieldSkeleton labelWidth='72px' />
                <FieldSkeleton labelWidth='76px' />
            </QuoteFieldsGridSkeleton>
            <SkeletonBlock $height='100%' $radius='6px' />
        </EditorShellSkeleton>
        <FixedQuoteFooterSkeleton>
            <SkeletonBlock $width='124px' $height='36px' $radius='6px' />
            <SkeletonBlock $width='82px' $height='36px' $radius='6px' />
        </FixedQuoteFooterSkeleton>
    </>
);
